import { useState } from "react";
import {
  Check,
  FileText,
  Tablet,
  Zap,
  BookOpen,
  Lock,
  Loader2,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const valueItems = [
  {
    icon: FileText,
    text: "Undated Daily, Weekly, and Monthly layout sheets (US Letter, A4, and A5 PDF formats)",
  },
  {
    icon: Tablet,
    text: "Interactive Tablet Edition (Pre-linked for GoodNotes, Notability, and Samsung Notes)",
  },
  {
    icon: Zap,
    text: "The Single-Screen Energy Filter Template",
  },
  {
    icon: BookOpen,
    text: "Step-by-Step ADHD-friendly Setup and Print Guide",
  },
];

export default function CheckoutSection() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [includeBump, setIncludeBump] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const totalPrice = includeBump ? 26 : 17;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError("Please fill in all fields.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const res = await fetch(`${API}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email,
          include_bump: includeBump,
          origin_url: originUrl,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || "Failed to create checkout session");
      }

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="checkout"
      data-testid="checkout-section"
      className="py-20 md:py-28"
      style={{ backgroundColor: "#1e1e1f" }}
    >
      <div className="max-w-6xl mx-auto px-5 md:px-12">
        {/* Section heading */}
        <h2
          className="text-2xl sm:text-3xl lg:text-4xl font-medium leading-tight mb-14 text-center"
          style={{ fontFamily: "'Fraunces', serif", color: "#e0e0e0" }}
          data-testid="checkout-heading"
        >
          Download your files and start organizing without the pressure today.
        </h2>

        {/* Two column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* LEFT - Value List */}
          <div className="lg:sticky lg:top-8 self-start">
            <h3
              className="text-xl font-medium mb-6"
              style={{ fontFamily: "'Fraunces', serif", color: "#e0e0e0" }}
            >
              What is included in The Soft Reset ($17):
            </h3>

            <div className="space-y-4 mb-8">
              {valueItems.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-start gap-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: "rgba(124, 58, 237, 0.12)" }}
                    >
                      <Icon className="w-4 h-4" style={{ color: "#7C3AED" }} />
                    </div>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ fontFamily: "'Inter', sans-serif", color: "#e0e0e0" }}
                    >
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Price */}
            <div
              className="rounded-xl p-6"
              style={{
                backgroundColor: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <div className="flex items-baseline gap-2 mb-2">
                <span
                  className="text-3xl font-bold"
                  style={{ fontFamily: "'Fraunces', serif", color: "#e0e0e0" }}
                  data-testid="price-display"
                >
                  ${totalPrice}
                </span>
                <span
                  className="text-sm"
                  style={{ color: "#b0b0b5", fontFamily: "'Inter', sans-serif" }}
                >
                  USD
                </span>
              </div>
              <p
                className="text-xs leading-relaxed"
                style={{ color: "#b0b0b5", fontFamily: "'Inter', sans-serif" }}
              >
                One-time payment. No hidden subscriptions. No recurring charges.
              </p>
            </div>
          </div>

          {/* RIGHT - Checkout Form */}
          <div>
            <form
              onSubmit={handleSubmit}
              className="rounded-xl p-6 md:p-8"
              style={{
                backgroundColor: "#1e1e1f",
                border: "1px solid #2e2e30",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
              }}
              data-testid="checkout-form"
            >
              {/* Order Bump */}
              <div
                className="rounded-lg p-4 mb-6"
                style={{
                  backgroundColor: "#252527",
                  border: "2px dashed #7C3AED",
                  borderRadius: "8px",
                }}
                data-testid="order-bump-box"
              >
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={includeBump}
                    onCheckedChange={(val) => setIncludeBump(!!val)}
                    className="mt-0.5 h-5 w-5 border-[#7C3AED] data-[state=checked]:bg-[#7C3AED] data-[state=checked]:border-[#7C3AED]"
                    data-testid="order-bump-checkbox"
                  />
                  <div>
                    <p
                      className="text-sm font-semibold mb-1"
                      style={{ color: "#e0e0e0", fontFamily: "'Inter', sans-serif" }}
                    >
                      Yes! Add the Dopamine Sticker Pack for $9.
                    </p>
                    <p
                      className="text-xs leading-relaxed"
                      style={{ color: "#b0b0b5", fontFamily: "'Inter', sans-serif" }}
                    >
                      Want to reward your brain for finishing tasks? Add these
                      functional, low-stimulus digital stickers to your templates
                      for a quick, satisfying dopamine boost without creating
                      visual clutter.
                    </p>
                  </div>
                </label>
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label
                    className="block mb-1.5 text-sm font-medium"
                    style={{ color: "#b0b0b5", fontFamily: "'Inter', sans-serif", fontSize: "14px" }}
                  >
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    data-testid="checkout-first-name"
                    className="w-full outline-none transition-all duration-200"
                    style={{
                      backgroundColor: "#2a2a2c",
                      border: "1px solid #3e3e40",
                      borderRadius: "8px",
                      color: "#ffffff",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "16px",
                      padding: "12px",
                      height: "48px",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#7C3AED";
                      e.target.style.boxShadow = "0 0 0 3px rgba(124,58,237,0.2)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#3e3e40";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>
                <div>
                  <label
                    className="block mb-1.5 text-sm font-medium"
                    style={{ color: "#b0b0b5", fontFamily: "'Inter', sans-serif", fontSize: "14px" }}
                  >
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                    data-testid="checkout-last-name"
                    className="w-full outline-none transition-all duration-200"
                    style={{
                      backgroundColor: "#2a2a2c",
                      border: "1px solid #3e3e40",
                      borderRadius: "8px",
                      color: "#ffffff",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "16px",
                      padding: "12px",
                      height: "48px",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#7C3AED";
                      e.target.style.boxShadow = "0 0 0 3px rgba(124,58,237,0.2)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#3e3e40";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>
              </div>

              {/* Email */}
              <div className="mb-4">
                <label
                  className="block mb-1.5 text-sm font-medium"
                  style={{ color: "#b0b0b5", fontFamily: "'Inter', sans-serif", fontSize: "14px" }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="checkout-email"
                  className="w-full outline-none transition-all duration-200"
                  style={{
                    backgroundColor: "#2a2a2c",
                    border: "1px solid #3e3e40",
                    borderRadius: "8px",
                    color: "#ffffff",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "16px",
                    padding: "12px",
                    height: "48px",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#7C3AED";
                    e.target.style.boxShadow = "0 0 0 3px rgba(124,58,237,0.2)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#3e3e40";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              {/* Info note about secure checkout */}
              <div
                className="flex items-center gap-2 mb-6 rounded-lg p-3"
                style={{ backgroundColor: "rgba(124, 58, 237, 0.06)" }}
              >
                <Lock className="w-4 h-4 flex-shrink-0" style={{ color: "#7C3AED" }} />
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: "#b0b0b5", fontFamily: "'Inter', sans-serif" }}
                >
                  Card details are securely collected on Stripe's encrypted payment page.
                </p>
              </div>

              {/* Error */}
              {error && (
                <div
                  className="mb-4 rounded-lg p-3 text-sm"
                  style={{
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    color: "#ef4444",
                  }}
                  data-testid="checkout-error"
                >
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                data-testid="checkout-submit-button"
                className="w-full flex items-center justify-center gap-2 text-white font-bold transition-all duration-200 hover:opacity-90 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                style={{
                  background: "linear-gradient(135deg, #7C3AED, #6D28D9)",
                  borderRadius: "8px",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "18px",
                  height: "52px",
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Complete My Order - $${totalPrice}`
                )}
              </button>

              {/* Trust line */}
              <p
                className="mt-4 text-center text-xs leading-relaxed"
                style={{ color: "#b0b0b5", fontFamily: "'Inter', sans-serif" }}
                data-testid="checkout-trust-line"
              >
                Secure checkout powered by Stripe. 256-bit encryption. | One-time
                payment. Lifetime access. Free updates forever. No recurring fees.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
